// @ts-nocheck
import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis
} from 'recharts'
import { RtkDropzone } from 'src/components/RtkDropzone'
import { RootState } from 'src/redux/root'
import {
  AdoptionConfigMode,
  AdoptionConfigModeOptions,
  Breakdown,
  EndUseConfig,
  EndUses,
  Metric,
  MetricConfig
} from 'src/redux/slices/analysis/consts'
import {
  mutationFixedCacheKeys,
  uploadAdoptionGisFile
} from 'src/redux/slices/analysis/endpoints'
import {
  setAdoptionCacheKey,
  setAdoptionMode,
  setBaselineAssignment,
  setEcmAssignment
} from 'src/redux/slices/analysis/reducers'
import {
  canComputeAdoption,
  makeSelectEcmAssignment,
  selectAdoptionCacheIsValid,
  selectBaselineAssignment,
  selectBreakdown,
  selectCopScalars,
  selectDuplicateAdoptionAssignments,
  selectMetric,
  selectMultiplierSeries,
  selectSortedScenarioNames
} from 'src/redux/slices/analysis/selectors'
import { AdoptionData } from 'src/redux/slices/analysis/types'
import { AsyncTask } from 'src/redux/slices/async-processing/consts'
import { startAsyncTask } from 'src/redux/slices/async-processing/reducers'

import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Collapse from '@material-ui/core/Collapse'
// MUI
import Grid from '@material-ui/core/Grid'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import Alert from '@material-ui/lab/Alert'
import ToggleButton from '@material-ui/lab/ToggleButton'
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup'

import AdoptionControl, { AdoptionControls } from '../components/AdoptionControl'
import AnalysisDropdownSelect, { AnalysisDropdown } from '../components/AnalysisDropdownSelect'
import ScenarioSelector from '../components/ScenarioSelector'
import TargetLines from '../components/TargetLines'

const scenarioGridWidth = 6 as 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
const scenarioParameterWidth = (12 - scenarioGridWidth) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

export default () => {
  const dispatch = useDispatch()
  const scenarioNames = useSelector(selectSortedScenarioNames)
  const duplicateAssignmentError = useSelector(selectDuplicateAdoptionAssignments)
  const adoptionSettings = useSelector((state: RootState) => state.analysis.adoption)
  const canCompute = useSelector(canComputeAdoption)
  const multipliers = useSelector(selectMultiplierSeries)
  const copScalars = useSelector(selectCopScalars)
  const metric = useSelector(selectMetric)

  const data = useSelector((state: RootState) => state.analysis.adoption.data)
  const [mutateAdoptionGisConfig, { data: gisConfigData }] = uploadAdoptionGisFile.useMutation({
    fixedCacheKey: mutationFixedCacheKeys.adoptionGisConfigFile,
  })

  const longTarget = useSelector((state: RootState) => state.analysis.targets.long.year)

  const computeAdoption = useCallback(() => {
    const body = {
      ...adoptionSettings,
      years: longTarget - new Date().getFullYear() + 3,
      multipliers,
      copScalars,
      metric,
    }
    dispatch(setAdoptionCacheKey(metric))
    dispatch(startAsyncTask({ endpoint: AsyncTask.computeAdoption, body }))
  }, [adoptionSettings, longTarget, multipliers, copScalars, dispatch])

  // Bail out
  if (scenarioNames.length < 2) {
    return (
      <Typography>
        You must upload at least two scenarios to run the adoption simulations.
      </Typography>
    )
  }
  return (
    <Grid container spacing={3}>
      <Grid item container spacing={2} direction='column' xs={4} alignItems='center'>
        <Grid item>
          <ToggleButtonGroup
            onChange={(e, value) => {
              if (value) dispatch(setAdoptionMode(value))
            }}
            value={adoptionSettings.mode}
            exclusive
          >
            {AdoptionConfigModeOptions.map(({ value, label }) => (
              <ToggleButton key={`${value}-adoption-config-mode-toggle`} value={value}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Grid>
        <Grid item>
          <Collapse in={adoptionSettings.mode === AdoptionConfigMode.GIS}>
            <div>
              <RtkDropzone
                accept={
                  'application/zip, .geojson, application/json, application/x-zip-compressed, multipart/x-zip,' +
                  ' "multipart/form-data", .cpg, .dbf, .prj, .qpj, .shp, .shx'
                }
                maxFiles={6}
                label='GIS Upload'
                skipReset={true}
                index={mutationFixedCacheKeys.adoptionGisConfigFile}
                mutation={uploadAdoptionGisFile.useMutation}
              />
            </div>
          </Collapse>
        </Grid>
        <Grid item container spacing={3}>
          <Grid item xs={scenarioGridWidth}>
            <ScenarioSelector
              label='Baseline'
              helperText='Initial state'
              selector={selectBaselineAssignment}
              action={setBaselineAssignment}
              error={duplicateAssignmentError}
            />
          </Grid>
          <Grid item xs={scenarioParameterWidth}>
            <AdoptionControl type={AdoptionControls.baseline} />
          </Grid>
        </Grid>
        <Grid item container spacing={3}>
          <Grid item xs={scenarioGridWidth}>
            <ScenarioSelector
              label={`ECM${scenarioNames.length > 2 ? 1 : ''}`}
              helperText={`Upgrade Package${scenarioNames.length > 2 ? ' 1' : ''}`}
              selector={makeSelectEcmAssignment(0)}
              action={(assignment) => setEcmAssignment({ scenario: assignment, index: 0 })}
              error={duplicateAssignmentError}
            />
          </Grid>
          <Grid item xs={scenarioParameterWidth}>
            <AdoptionControl type={AdoptionControls.ecm} index={0} />
          </Grid>
        </Grid>
        {scenarioNames.length > 2 && (
          <Grid item container spacing={3}>
            <Grid item xs={scenarioGridWidth}>
              <ScenarioSelector
                label='ECM2'
                helperText='Upgrade Package 2'
                selector={makeSelectEcmAssignment(1)}
                action={(assignment) => setEcmAssignment({ scenario: assignment, index: 1 })}
                error={duplicateAssignmentError}
              />
            </Grid>
            <Grid item xs={scenarioParameterWidth}>
              <AdoptionControl type={AdoptionControls.ecm} index={1} />
            </Grid>
          </Grid>
        )}
        <Grid item style={{ width: '100%' }}>
          <Collapse in={adoptionSettings.mode === AdoptionConfigMode.GIS}>
            <div>
              <AdoptionControl type={AdoptionControls.name} />
            </div>
          </Collapse>
        </Grid>
        <Grid item>
          <AnalysisDropdownSelect dropdown={AnalysisDropdown.Metric} />
        </Grid>
        <Grid item>
          <ConditionalTooltip
            showTooltip={!canCompute}
            title={'You must assign fields before computing adoption.'}
          >
            <Button onClick={computeAdoption} disabled={!canCompute} variant='contained'>
              Compute
            </Button>
          </ConditionalTooltip>
        </Grid>
        <Grid item />
      </Grid>
      <Grid item container direction='column' justifyContent='center' alignItems='center' xs={8}>
        {data && <AdoptionChartWithPng data={data} />}
        {data && (
          <Grid item xs style={{ width: '30%' }}>
            <AnalysisDropdownSelect dropdown={AnalysisDropdown.AdoptionBreakdown} />
          </Grid>
        )}
        <Grid item xs />
      </Grid>
    </Grid>
  )
}

type AdoptionChartProps = {
  data: AdoptionData
}
export const AdoptionChartWithPng: React.FC<AdoptionChartProps> = ({ data }) => {
  const breakdown = useSelector(selectBreakdown)
  const { cacheIsValid, metric } = useSelector(selectAdoptionCacheIsValid)

  if ([Breakdown.Archetype, Breakdown.Source].includes(breakdown)) {
    return <Typography variant='overline'>No data available for the selected breakdown</Typography>
  }
  return (
    <>
      <Collapse in={!cacheIsValid}>
        <Alert severity='warning'>
          The current simulation data is stale because time series or fuel parameter settings have
          changed.
        </Alert>
      </Collapse>
      <Typography variant='h5'>{metric} Projection</Typography>
      <Box height={'55vh'} width={'100%'}>
        <ResponsiveContainer width={'99%'} height={'99%'}>
          <ComposedChart
            width={800}
            height={800}
            data={data}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
            stackOffset={'none'}
          >
            <CartesianGrid />
            <XAxis dataKey='Year' />
            <YAxis
              domain={['auto', 'auto']}
              label={{
                value: `${MetricConfig[metric ?? 'Carbon']?.label} (${
                  MetricConfig[metric ?? 'Carbon']?.units
                })`,
                angle: -90,
                position: 'insideBottomLeft',
                textAnchor: 'middle',
              }}
              width={100}
            />
            <Legend verticalAlign='top' height={36} />
            <RechartsTooltip
              cursor={false}
              formatter={MetricConfig[metric ?? 'Carbon']?.formatter}
            />
            {breakdown === Breakdown.Total && (
              <>
                <Line
                  dataKey={'Total_lower_whisker'}
                  // stroke={useColors[use]}
                  // type="basis"
                  strokeDasharray='3 3'
                  dot={false}
                />
                <Line
                  dataKey={'Total_upper_whisker'}
                  // stroke={useColors[use]}
                  // type="basis"
                  strokeDasharray='3 3'
                  dot={false}
                />
                <Line
                  dataKey={'Total_median'}
                  // stroke={useColors[use]}
                  type='basis'
                  dot={false}
                />
                <Area
                  dataKey={'Total'}
                  type='basis'
                  // stroke={useColors[use]}
                  // fill={useColors[use]}
                />
              </>
            )}
            {breakdown === Breakdown.Use &&
              Object.entries(EndUseConfig).map(([endUse, { color }]) => (
                <Area
                  key={`${endUse}-area`}
                  dataKey={`${endUse}_median`}
                  type='basis'
                  stackId={1}
                  stroke={color}
                  fill={color}
                />
              ))}
            {/* {TargetLines()} */}
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </>
  )
}

type ConditionalTooltipProps = {
  title: string
  showTooltip: boolean
}
const ConditionalTooltip: React.FC<React.PropsWithChildren<ConditionalTooltipProps>> = ({
  title,
  showTooltip,
  children,
}) => {
  if (showTooltip) {
    return (
      <Tooltip title={title}>
        <span>{children}</span>
      </Tooltip>
    )
  } else {
    return <>{children}</>
  }
}
